import React, { useState, useRef, useEffect } from 'react'
import Table from '.'
import { SvgReceipt } from '../svg'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TransactionStatus } from '../status'
import { convertPageToOffsetLimit, formatMoney } from '../../utils';
import { API_DEFAULT_PAGE_SIZE } from '../../const';
import useFilter from '../../hooks/filter';
import Filters from '../filters';
import { getTransactions } from '../../slices/transactions';
import { getSubscriptions } from '../../slices/subscriptions';
import { ExportModal } from '../export-modal';
import { Button } from '../button';
import useModal from '../../hooks/modal';
import moment from 'moment';
import SubscriptionsService from "../../api/subscriptions";


export const TransactionTable = ({ callBack, size, filters, limit, hideFilters, addonSearchParams }) => {

    const dispatch = useDispatch()
    const navigate = useNavigate();

    const transactions = useSelector((state) => state.transactions)
    const subscriptions = useSelector((state) => state.subscriptions.results)

    let { transactionId } = useParams();
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(API_DEFAULT_PAGE_SIZE)
    const [isDirty, setIsDirty] = useState(true)
    const [isShowingExportModal, toggleShowingExportModal] = useModal();
    const fetchTransactionsPromise = useRef()
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        activeFilters,
        addFilter,
        removeFilter,
        changeFilterValue,
        replaceFilter,
        updateSearchFilter,
    } = useFilter([])

    const filterTypes = [
        {
            type: 'multichoice',
            key: 'state',
            label: gettext('State'),
            choices: [
                { label: gettext('Settled'), value: 'settled' },
                { label: gettext('Failed'), value: 'failed' },
                { label: gettext('Refunded'), value: 'refunded' },
                { label: gettext('Pending'), value: 'pending' },
            ]
        },
        {
            type: 'async_pillselector',
            key: 'subscription_id',
            label: gettext('Subscription'),
            loadOptions: (query) => {
                return new Promise(resolve =>
                    resolve(SubscriptionsService.getSubscriptions([{ 'search': query }]))
                ).then(response => {
                    return response.results.map(subscription => {
                        return { label: `${subscription.customer.name} - ${subscription.plan.name} (${subscription.amount} ${subscription.currency})`, value: subscription.id }
                    })
                })
            }
        },
        {
            type: 'daterange',
            key: 'created_at',
            label: gettext('Created in range'),
        }
    ]

    useEffect(() => {
        const subscription = searchParams.get('subscription')
        if (subscription && !activeFilters.find(f => f.subscription_id === subscription)) {
            addFilter({ subscription_id: subscription })
        }
    }, [])

    const columns = React.useMemo(
        () => [
            {
                Header: gettext('State'),
                accessor: 'transaction.data.status',
                className: "text-center",
                Cell: ({ row }) => {
                    if (row.original) {
                        return <TransactionStatus transaction={row.original} showText={false} />
                    } else {
                        return '-'
                    }
                }
            },
            {
                Header: gettext('Transaction number'),
                accessor: 'transaction.uuid',
                className: "text-right",
                Cell: ({ row }) => {
                    let uuid = row.original.uuid
                    if (uuid) {
                        return row.original.uuid
                    } else {
                        return '-'
                    }
                }
            },
            {
                Header: gettext('Date / time'),
                accessor: 'created_at',
                Cell: ({ row }) => {
                    return moment(row.original.created_at).format('DD.MM.yyyy/HH:mm')
                }
            },
            {
                Header: gettext('Amount'),
                accessor: 'transaction.amount.amount',
                className: "text-end",
                Cell: ({ row }) => {
                    let amount = row.original.amount
                    if (amount) {
                        return formatMoney(amount)
                    } else {
                        return '-'
                    }
                }

            },
            {
                Header: gettext('Currency'),
                accessor: 'transaction.amount.currency',
                className: "text-right",
                Cell: ({ row }) => {
                    let currency = row.original.currency
                    if (currency) {
                        return currency
                    } else {
                        return '-'
                    }
                }

            },
            {
                Header: gettext('Receipt'),
                accessor: 'id',
                className: "text-center",
                Cell: ({ row }) => {
                    return row.original.state === 'settled' ? <a href={`/apiv2/transactions/${row.original.id}/receipt/`} onClick={(e) => e.stopPropagation()} target="_blank"><SvgReceipt /></a> : null
                }
            }
        ])

    const onRowClick = (row, rowInfo, column, instance) => {
        if(callBack){
            callBack(true)
        }
        if (searchParams.size > 0) {
            navigate(`/transactions/${row.original.id}/?${searchParams.toString()}`)
        } else if (addonSearchParams) {
            navigate(`/transactions/${row.original.id}/?${addonSearchParams}`)
        } else {
            navigate(`/transactions/${row.original.id}/`)
        }
    }

    useEffect(() => {
        setIsDirty(true)
        setCurrentPage(1)
    }, [activeFilters])
    useEffect(() => {
        setIsDirty(true)
    }, [currentPage])
    useEffect(() => {
        setIsDirty(true)
    }, [pageSize])

    useEffect(() => {
        if (isDirty) {
            const offsetLimit = convertPageToOffsetLimit(currentPage, pageSize)
            const params = [...activeFilters, ...offsetLimit, ...filters]

            fetchTransactionsPromise.current?.abort()
            let ignore = false
            if (!ignore) {
                fetchTransactionsPromise.current = dispatch(getTransactions(params))
                setIsDirty(false)
            }
            return () => {
                ignore = true;
            };
        }
    }, [isDirty])

    return (
        <>
            {!hideFilters &&
                <>
                    <Filters
                        addFilter={addFilter}
                        activeFilters={activeFilters}
                        filterTypes={filterTypes}
                        changeFilterValue={changeFilterValue}
                        removeFilter={removeFilter}
                        replaceFilter={replaceFilter}
                        updateSearchFilter={updateSearchFilter}
                    />
                    <div className="row mt-3 m-0">
                        <div className="col-12 bg-02 pe-0 rounded-2">
                            <div className="row align-items-center">
                                <div className="col flex-grow-1">
                                    {transactions.loading ?
                                        <>
                                            <div className="loader loader-small" role="status"></div>
                                        </>
                                        :
                                        <>{transactions.count} {gettext('results')}</>
                                    }
                                </div>
                                <div className="col-2 min-width-116">
                                    <Button className="btn btn-primary btn-fluid btn-sm ml-3" onClick={toggleShowingExportModal}>{gettext('Export')}</Button>
                                    <ExportModal
                                        show={isShowingExportModal}
                                        onClose={toggleShowingExportModal}
                                        model={'Transaction'}
                                        filters={activeFilters}
                                        defaultTitle={gettext('Transactions')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
            <div className="row mt-3">
                <div className="col-12 overflow-scroll">
                    {transactions.results.length > 0 &&
                        <Table
                            columns={columns}
                            data={limit ? transactions.results.slice(0, limit) : transactions.results}
                            onRowClick={onRowClick}
                            selectedRow={transactionId}
                            loading={transactions.loading}
                            totalRows={limit ? limit : transactions.count}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageChangeHandler={(page, pageSize) => {
                                setCurrentPage(page)
                                setPageSize(pageSize)
                            }}
                        />
                    }
                </div>
            </div>
        </>
    )
}

TransactionTable.propTypes = {
    size: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.object),
};

TransactionTable.defaultProps = {
    size: 'md',
    filters: [],
};